import React, { useState, useEffect } from 'react';
import './ValueTriangle.css';
import triangleImg from "../assets/triangle.png";
import { Users, Workflow, Cpu } from "lucide-react";

export default function ValueTriangle() {
    const [activeVertex, setActiveVertex] = useState(null);

    useEffect(() => {
        const section = document.querySelector(".value-triangle-section");
        section.style.opacity = "0";
        section.style.transform = "translateY(30px)";
        section.style.transition = "all 1s ease-out";

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    section.style.opacity = "1";
                    section.style.transform = "translateY(0)";
                }
            },
            { threshold: 0.2 }
        );

        observer.observe(section);
        return () => observer.disconnect();
    }, []);

    const handleSetActive = (type) => {
        setActiveVertex((prev) => (prev === type ? null : type));
    };

    return (
        <section className="value-triangle-section">
            <div className="container">
                {/* Section Header */}
                <div className="about-section-header">
                    <h2>Cospin's 3Ps Maturity Model</h2>
                    <p className="about-section-intro">
                        At Cospin, we believe every successful product is built on the 3Ps. True impact comes from a systems-thinking approach, where no single pillar alone ensures success. We help organizations strike the right balance across these three pillars to evolve and achieve greater outcomes.
                    </p>
                </div>

                {/* Main Content */}
                <div className="triangle-main-content">
                    {/* Triangle Visual */}
                    <div className="triangle-visual">
                        <img
                            src={triangleImg}
                            alt="Cospin Value Triangle"
                            className="triangle-3d-image"
                            style={{
                                filter:
                                    activeVertex !== null
                                        ? "drop-shadow(0 15px 35px rgba(219, 166, 4, 0.3)) brightness(1.05)"
                                        : "drop-shadow(0 15px 35px rgba(6, 9, 74, 0.2))",
                            }}
                        />

                        <div className="vertex-overlay">
                            <div className="vertex vertex-people" data-vertex="people">
                                <Users size={20} color="white" />
                            </div>
                            <div className="vertex vertex-process" data-vertex="process">
                                <Workflow size={20} color="white" />
                            </div>
                            <div className="vertex vertex-technology" data-vertex="technology">
                                <Cpu size={20} color="white" />
                            </div>

                            {/* Animated Lines */}
                            <div className="connection-line line-1"></div>
                            <div className="connection-line line-2"></div>
                            <div className="connection-line line-3"></div>
                        </div>
                    </div>

                    {/* Content Side */}
                    <div className="triangle-content">
                        <div
                            className={`pillar-item ${activeVertex === "people" ? "highlighted" : ""}`}
                            onMouseEnter={() => handleSetActive("people")}
                            onMouseLeave={() => handleSetActive(null)}
                        >
                            <div className="pillar-header">
                                <div className="pillar-icon">
                                    <Users size={20} color="white" />
                                </div>
                                <div>
                                    <h3 className="pillar-title">People</h3>
                                    <p className="pillar-subtitle">Talent-First. Global mindset.</p>
                                </div>
                            </div>
                            <p className="pillar-description">
                                Precision hiring and smart retention strategies. We glocalize—building teams with a global mindset rooted in local values.
                            </p>
                        </div>

                        <div
                            className={`pillar-item ${activeVertex === "process" ? "highlighted" : ""}`}
                            onMouseEnter={() => handleSetActive("process")}
                            onMouseLeave={() => handleSetActive(null)}
                        >
                            <div className="pillar-header">
                                <div className="pillar-icon">
                                    <Workflow size={20} color="white" />
                                </div>
                                <div>
                                    <h3 className="pillar-title">Process</h3>
                                    <p className="pillar-subtitle">Eliminate waste. Continuous improvement.</p>
                                </div>
                            </div>
                            <p className="pillar-description">
                                Iterative development process, Lean thinking and battle-tested playbooks for execution
                                excellence. Our frameworks ensure consistent delivery and
                                continuous improvement.
                            </p>
                        </div>

                        <div
                            className={`pillar-item ${activeVertex === "technology" ? "highlighted" : ""}`}
                            onMouseEnter={() => handleSetActive("technology")}
                            onMouseLeave={() => handleSetActive(null)}
                        >
                            <div className="pillar-header">
                                <div className="pillar-icon">
                                    <Cpu size={20} color="white" />
                                </div>
                                <div>
                                    <h3 className="pillar-title">Product</h3>
                                    <p className="pillar-subtitle">Customer value proposition.</p>
                                </div>
                            </div>
                            <p className="pillar-description">
                                Roadmap that is driven by deep understanding of business domain, user research,
                                worldclass experience and success metrics.
                                We leverage the latest innovations to create competitive advantages.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
